// AddJob — modal-embedded form for creating a Job row.
// Refactored 2026-05-05 (Phase 3.3, Class P) onto ModalFormHeader.

import React, { useState } from 'react';
import CTextField from '../CTextField/CTextField';
import { Button, Alert, Stack, Box } from '@mui/material';
import { WorkOutlineRounded } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import CSelect from '../CSelect/CSelect';
import { JOB_STATUS_OPTIONS, JOB_STATUS_DEFAULT } from '../../utils/jobStatus';
import { createJob, getJobs } from '../../feature/jobSlice';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { validateInput } from '../../utils/validateInput';
import { ModalFormHeader, FieldLabel } from '../PaperForm';

const AddJob = (props) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSlice.token);
  const [jobName, setJobName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  // Form parity 2026-09-29: jobs.status is NOT NULL enum('in_process','completed','priority')
  // in production. '' (nothing picked) and 'on_hold' were rejected by MySQL, so the job was
  // never created. Default to the server default and offer only the three stored values.
  const [status, setStatus] = useState(JOB_STATUS_DEFAULT);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(null);
  // 2026-05-05 (Dean QA report bug #4) — inline error fallback.
  // The validateInput() call already fires a toast on invalid input but the
  // toast can be obscured by the modal overlay. Surfacing the error inline
  // at the top of the form gives the user an unmissable, persistent signal.
  const [formError, setFormError] = useState(null);

  const getTenantId = () => {
    try {
      return JSON.parse(atob(token.split('.')[1])).tenantId;
    } catch (e) {
      return null;
    }
  };

  const selectData = JOB_STATUS_OPTIONS;

  const handleSubmit = () => {
    setFormError(null);
    if (!jobName || !jobName.trim()) {
      setFormError('Job Name is required.');
      validateInput('jobName', jobName); // also fires the toast (legacy path)
      return;
    }
    if (!validateInput('jobName', jobName)) {
      setFormError('Please enter a valid Job Name.');
      return;
    }
    const jobData = {
      name: jobName,
      description: jobDescription,
      status: status,
      startDate: startDate,
      endDate: endDate,
      tenantId: getTenantId(),
    };
    dispatch(createJob(jobData)).unwrap().then(() => {
      dispatch(getJobs());
      if (props.setOpen) props.setOpen(false);
    }).catch((err) => {
      const msg = err?.message || err?.response?.data?.message || 'Create job failed. Please retry.';
      setFormError(msg);
    });
  };

  return (
    <Box>
      <ModalFormHeader
        overline="SFM · Job"
        title="Add Job"
        icon={WorkOutlineRounded}
      />
      {formError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
          {formError}
        </Alert>
      )}
      <Stack spacing={2}>
        <CTextField label="Job name" name="name" value={jobName} setValue={setJobName} required />
        <CTextField label="Description" name="description" value={jobDescription} setValue={setJobDescription} />
        <Box>
          <FieldLabel>Status</FieldLabel>
          <CSelect data={selectData} label="" initialValue={status} setValue={setStatus} />
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={['DatePicker']}>
            <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
              <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} />
              <DatePicker label="End Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} />
            </Stack>
          </DemoContainer>
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            width: '100%',
            mt: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '0.01em',
          }}
        >
          Create Job
        </Button>
      </Stack>
    </Box>
  );
};

export default AddJob;
